import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  HStack,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text
} from '@chakra-ui/react'
import { CustomCollapse, InputFormControl, NumberFormControl, RadioGroupFormControl, SelectFormControl } from 'BrokerTsLib/src/components'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import type { CalcMinAmountRealtyRequest, InsuranceParamsPropertyDto, SecondaryRealtyDto } from 'BrokerTsLib/src/dtos'
import { Insurer, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFieldArray, useFormContext } from 'BrokerTsLib/src/rhf'
import React, { useState } from 'react'

import { CustomNumberInput, FormLabel, InputGroup } from '@/components'
import { HorizontalRadios } from '@/components/HorizontalRadios'
import { FormRow } from '@/containers/forms/FormRow'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useInsurers, useSelections } from '@/hooks'
import { useCalcRecommendedAmountRealty } from '@/hooks/useWizard'
import { Calculate, Plus, QuestionMark, Trash } from '@/icons'

export const RealtyForm = () => {
  const { isDisabled } = useInsuranceParams()
  const { isInsurerAvailable, isInsurersAvailable } = useInsurers(false, null)
  const { data: dataTypKonstrukce } = useSelections(SelectionType.TypKonstrukce)
  const { data: dataStrechy } = useSelections(SelectionType.TypStrechy)
  const { data: dataOwner } = useSelections(SelectionType.Vlastnictvi)
  const { data: dataApparmentType } = useSelections(SelectionType.TypBytovehoDomu)
  const { data: dataBasementArea } = useSelections(SelectionType.PlochaPodsklepení)
  const { data: dataDokonceni } = useSelections(SelectionType.DokonceniNemovitosti)
  const { data: dataStav } = useSelections(SelectionType.Stav)
  const { data: dataBuildingType } = useSelections(SelectionType.DalsiNemovitost)

  const {
    setValue,
    register,
    watch,
    getValues,
    formState: { errors },
    control
  } = useFormContext<InsuranceParamsPropertyDto>()

  const selectedRealtyType = watch('property.data.realtyTypeId')
  const houseWithBasementValue = watch('property.data.hasBasement')
  const roofType = watch('property.data.realty.roofType')
  const residentialRoof = watch('property.data.realty.isRoofResidential')
  const secondaryRealty = watch('property.data.realty.secondaryRealty')

  const {
    fields: fieldsSecondaryRealties,
    remove: removeSecondaryRealty,
    append: appendSecondaryRealty,
  } = useFieldArray({
    name: 'property.data.secondaryRealties',
    control: control,
  })

  let rokKolaudaceHint = 'Uveďte rok kolaudace/uvedení do provozu nebo rok poslední rekolaudace.'
  const kolaudaceInsurers = []
  if (isInsurerAvailable(Insurer.Allianz)) {
    kolaudaceInsurers.push('Allianz')
  }
  if (isInsurerAvailable(Insurer.Pillow)) {
    kolaudaceInsurers.push('Pillow')
  }
  if (isInsurerAvailable(Insurer.Slavia)) {
    kolaudaceInsurers.push('Slavia')
  }
  if (isInsurerAvailable(Insurer.Uniqa)) {
    kolaudaceInsurers.push('Uniqa')
  }
  if (kolaudaceInsurers.length > 0) {
    rokKolaudaceHint += ` Údaj je povinný pro pojišťovny: ${kolaudaceInsurers.join(', ')}.`
  }

  let builtUpAreaHint = 'Součet plochy všech podlaží, včetně balkonů, teras, sklepů a půdních prostor, bez ohledu na to, zda jsou využívány'
  if (isInsurerAvailable(Insurer.KB) && !!selectedRealtyType && selectedRealtyType == Selection.RodinnyDum) {
    builtUpAreaHint += '. Pro pojišťovnu Komerční pojišťovna: V případě půdních prostor, započtěte pouze obytnou část.'
  }

  let atticAreaTooltip = ''
  if (isInsurerAvailable(Insurer.Kooperativa) || isInsurerAvailable(Insurer.Csob)) {
    atticAreaTooltip = 'Pojišťovna '
    if (isInsurerAvailable(Insurer.Kooperativa)) {
      atticAreaTooltip += 'Kooperativa '
      if (isInsurerAvailable(Insurer.Csob)) {
        atticAreaTooltip += 'a ČSOB '
      }
    }
    else {
      atticAreaTooltip += 'ČSOB '
    }
    atticAreaTooltip += 'následně nezahrnuje neobytné podkroví do celkové podlahové plochy.'
  }

  const isOpenApartment = !!selectedRealtyType && (selectedRealtyType == Selection.Byt || selectedRealtyType == Selection.NajemniByt)
  const isOpenApartmentOwned = !!selectedRealtyType && selectedRealtyType == Selection.Byt
  const isOpenRoofSedlova = roofType == Selection.StrechaSedlova
  const isOpenRoofNotResidential = residentialRoof == false

  const [calcRealtyAmountError, setCalcRealtyAmountError] = useState('')
  const { mutateAsync: calcRecommendedRealtyAmount } = useCalcRecommendedAmountRealty()


  const calcRealtyAmount = () => {
    const req: CalcMinAmountRealtyRequest = {
      builtUpArea: getValues('property.data.realty.builtUpArea'),
      builtUpCadastralArea: getValues(
        'property.data.realty.builtUpCadastralArea',
      ),
      usefulArea: getValues('property.data.realty.usefulArea'),
      realtyType: getValues('property.data.realtyTypeId'),
      appartmentType: getValues('property.data.realty.apparmentType'),
      hasBasement: getValues('property.data.hasBasement'),
      basementArea: getValues('property.data.realty.basementArea'),
      floorCount: getValues('property.data.realty.floorsCount'),
      roofType: getValues('property.data.realty.roofType'),
      isRoofResidential: getValues('property.data.realty.isRoofResidential'),
      finishState: getValues('property.data.realty.finishState'),
      structuralType: getValues('property.data.realty.structuralType'),
    }

    calcRecommendedRealtyAmount(req).then((resp) => {
      if (resp.data.amount) {
        setValue('property.data.realty.realtyAmount', resp.data.amount!)
        setCalcRealtyAmountError('')
      } else {
        setCalcRealtyAmountError(resp.data.message)
      }
    })
  }

  return (
    <Grid rowGap={12} w='100%'>
      <FormRow itemsCount={1}>
        <CustomCollapse in={isOpenApartmentOwned}>
          {/* Vlastnictví */}
          <RadioGroupFormControl name='property.data.realty.owner' valueAsNumber isRequired
            label='Vlastnictví'
            labelProps={{
              hint: <>
                Osobní vlastnictví bytu - pojištěný je zapsán jako
                vlastník v katastru nemovitostí.
                <br />
                Družstevní vlastnictví bytu - vlastníkem družstevního
                bytu je vždy družstvo (bytové, stavební bytové apod.),
                které s družstevníkem, tedy nájemcem uzavírá nájemní
                smlouvu. Některé pojišťovny umožňují takovému
                nájemníkovi pojistit nejen domácnost, ale i stavbu -
                bytovou jednotku. V takovém případě se jedná o
                pojištění cizího pojistného nebezpečí. Vlastník musí
                být s touto pojistnou smlouvou seznámen a případné
                právo na pojistné plnění má jen v případě, prokáže-li,
                že vlastníka bytu/jednotky, tzn. družstvo, se smlouvou
                seznámil a družstvo jako vlastník pojištěné věci s
                poskytnutím pojistného plnění vyjádřilo svůj souhlas.
              </>
            }}>
            <HorizontalRadios options={dataOwner?.data.map((item) => ({ value: item.id.toString(), label: item.description }))} />
          </RadioGroupFormControl>
        </CustomCollapse>

        <CustomCollapse in={isOpenApartment}>
          {/* Bytový dům */}
          <RadioGroupFormControl name='property.data.realty.apparmentType' valueAsNumber isRequired
            label='Bytový dům'
            labelProps={{
              hint: 'Typové bytové domy jsou domy realizované hromadnou výstavbou.'
            }}>
            <HorizontalRadios options={dataApparmentType?.data.map((item) => ({ value: item.id.toString(), label: item.description }))} />
          </RadioGroupFormControl>
        </CustomCollapse>

        <FormRow>
          {/* Zkolaudováno */}
          <RadioGroupFormControl name='property.data.realty.isApproved' valueAsBoolean isRequired
            label='Zkolaudováno'
            isDisabled={isDisabled}
          >
            <HorizontalRadios options={[{ value: 'true', label: 'Ano' }, { value: 'false', label: 'Ne' }]} />
          </RadioGroupFormControl>

          {/* Rok kolaudace či výstavby */}
          <NumberFormControl
            name='property.data.realty.approvedYear'
            label='Rok kolaudace či výstavby'
            labelProps={{ hint: rokKolaudaceHint }}
            isRequired
            inputRightElement=''
            w='full'
            isDisabled={isDisabled}
          />
        </FormRow>
      </FormRow>

      <FormRow>
        {/* Celková podlahová plocha */}
        <NumberFormControl name='property.data.realty.builtUpArea' isRequired
          label='Celková podlahová plocha' labelProps={{ hint: builtUpAreaHint }}
          inputRightElement="㎡"
          w='100%'
          inputProps={{
            placeholder: 'Např. 128'
          }}
          isDisabled={isDisabled}
          onBlur={(value) => {
            if (!isInsurerAvailable(Insurer.KB)) {
              // pouze pokud není povolena pojišťovna KB, pro ni má být použitá jiná hodnota
              setValue('property.data.household.livingArea', value as number)
            }
          }}
        />

        {/* z toho určeno k podnikání */}
        <FormControl>
          <FormLabel tooltip="z toho určeno k podnikání">
            z toho určeno k podnikání
          </FormLabel>

          <CustomNumberInput
            w='100%'
            value={watch('property.data.realty.builtUpAreaBusiness') ?? undefined}
            onValueChange={(_, value) =>
              setValue(
                'property.data.realty.builtUpAreaBusiness',
                value,
              )
            }
            isDisabled={isDisabled}
            inputRightElement="%"
            inputProps={{
              placeholder: 'Např. 20'
            }}
          />
        </FormControl>

        <CustomCollapse in={isOpenApartment}>
          <NumberFormControl name='property.data.realty.usefulArea' isRequired
            label='Užitná plocha' labelProps={{ hint: 'Do užitné plochy se nezahrnuje balkón, lodžie, terasa, sklep, zahrádka atd.' }}
            inputRightElement="㎡"
            w='100%'
            inputProps={{
              placeholder: 'Např. 128'
            }}
            isDisabled={isDisabled}
            onBlur={(value) => {
              if (isInsurerAvailable(Insurer.KB)) {
                // pokud je povolená KB, nastavujeme plochu domácnosti bytu podle užitné plochy
                setValue('property.data.household.livingArea', value as number)
              }
            }}
          />
        </CustomCollapse>

        {/* Zastavěná plocha */}
        <FormControl
          isRequired
          isInvalid={
            !!errors.property?.data?.realty?.builtUpCadastralArea
          }
        >
          <FormLabel tooltip="Plocha ohraničená pravoúhlými průměty vnějšího líce obvodových konstrukcí všech nadzemních i podzemních podlaží do vodorovné roviny. Je uvedena v katastru nemovitostí.">
            Zastavěná plocha
          </FormLabel>

          <CustomNumberInput
            w='100%'
            defaultValue={getValues(
              'property.data.realty.builtUpCadastralArea',
            )}
            onValueChange={(_, value) =>
              setValue(
                'property.data.realty.builtUpCadastralArea',
                value,
              )
            }
            isDisabled={isDisabled}
            inputRightElement="㎡"
            inputProps={{
              placeholder: 'Např. 128'
            }}
          />

          {errors.property?.data?.realty?.builtUpCadastralArea && (
            <FormErrorMessage>
              {
                errors.property?.data?.realty?.builtUpCadastralArea
                  .message
              }
            </FormErrorMessage>
          )}
        </FormControl>
      </FormRow>

      <CustomCollapse in={!isOpenApartment}>
        <FormRow itemsCount={1}>
          <FormRow>
            {/* Počet nadzemních podlaží */}
            <FormControl
              isRequired
              isInvalid={!!errors.property?.data?.realty?.floorsCount}
            >
              <FormLabel tooltip="Do počtu nadzemních podlaží se nezapočítává podkroví.">
                Počet nadzemních podlaží
              </FormLabel>

              <InputGroup
                placeholder="Např. 1"
                {...register('property.data.realty.floorsCount')}
                errors={errors.property?.data?.realty?.floorsCount}
                isDisabled={isDisabled}
              />
            </FormControl>


            {/* Typ střechy */}
            <FormControl
              isInvalid={!!errors.property?.data?.realty?.roofType}
              isRequired
            >
              <FormLabel
                tooltip={
                  <>
                    Vyberte typ provedení střechy budovy.
                    <br />
                    Plochá střecha - střešní konstrukce nebo střecha
                    se sklonem nanejvýš 5°.
                    <br />
                    Sedlová střecha - všechny ostatní střechy
                    (označované jako např. sedlové, pultové, stanové,
                    mansardové, apod.), které nemůžeme označit jako
                    ploché. V prostoru pod sklonitou střechou se
                    nachází podkroví, které je umístěné nad posledním
                    nadzemním podlažím budovy.
                  </>
                }
              >
                Typ střechy
              </FormLabel>

              <RadioGroup
                name={register('property.data.realty.roofType').name}
                value={watch(
                  'property.data.realty.roofType',
                )?.toString()}
                onChange={(val) =>
                  setValue(
                    'property.data.realty.roofType',
                    Number(val),
                  )
                }
                isDisabled={isDisabled}
              >
                <HStack gap={6}>
                  {dataStrechy?.data.map((item) => (
                    <Radio
                      gap={4}
                      value={item.id.toString()}
                      key={item.id.toString()}
                    >
                      {item.description}
                    </Radio>
                  ))}
                </HStack>
              </RadioGroup>

              {errors.property?.data?.realty?.roofType && (
                <FormErrorMessage>
                  {errors.property?.data?.realty?.roofType.message}
                </FormErrorMessage>
              )}
            </FormControl>
          </FormRow>

          <CustomCollapse in={isOpenRoofSedlova}>
            <FormRow>
              {/* Je podkroví obytné */}
              <RadioGroupFormControl name='property.data.realty.isRoofResidential' valueAsBoolean isRequired
                label='Je podkroví obytné'
                isDisabled={isDisabled}
              >
                <HorizontalRadios options={[{ value: 'true', label: 'Ano' }, { value: 'false', label: 'Ne' }]} />
              </RadioGroupFormControl>

              <CustomCollapse
                in={
                  isOpenRoofSedlova &&
                  isOpenRoofNotResidential &&
                  (isInsurerAvailable(Insurer.Kooperativa) || isInsurerAvailable(Insurer.Csob))
                }
              >
                {/* Plocha podkroví */}
                <FormControl
                  isInvalid={!!errors.property?.data?.realty?.atticArea}
                >
                  <FormLabel tooltip={atticAreaTooltip}>
                    Plocha podkroví
                  </FormLabel>

                  <InputGroup
                    placeholder="Např. 128"
                    {...register('property.data.realty.atticArea')}
                    isDisabled={isDisabled}
                    rightElement="㎡"
                  />

                  {errors.property?.data?.realty?.atticArea && (
                    <FormErrorMessage>
                      {errors.property?.data?.realty?.atticArea.message}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </CustomCollapse>
            </FormRow>
          </CustomCollapse>


          {/* Provedení budovy */}
          <RadioGroupFormControl name='property.data.realty.finishState' valueAsNumber isRequired
            label='Provedení budovy'
            labelProps={{
              hint: <Stack gap={2}>
                <Text as='span'>
                  Rozhodujte podle toho, které prvky převažují.
                </Text>
                <Text as='span'>
                  <Text as='h4' fontWeight="bold" mr={1}>
                    Základní provedení
                  </Text>{' '}
                  jednotlivých konstrukcí se vyznačuje nižší
                  kvalitou materiálu, základy jsou bez izolací,
                  chybí elektroinstalace, bleskosvod, rozvody
                  vody, koupelna, WC je mimo budovu.
                </Text>
                <Text as='span'>
                  <Text as='h4' fontWeight="bold" mr={1}>
                    Standardní provedení
                  </Text>{' '}
                  jednotlivých konstrukcí využívá standardní
                  materiály, obsahuje standardní vnitřní vybavení,
                  jako jsou vana či sprchový kout, splachovací
                  záchod.
                </Text>
                <Text as='span'>
                  <Text as='h4' fontWeight="bold" mr={1}>
                    Luxusní provedení
                  </Text>{' '}
                  jednotlivých konstrukcí se vyznačuje zateplenou
                  fasádou a vnějšími obklady, vnitřní vybavení je
                  z exotických a nadstandardních materiálů.
                </Text>
              </Stack>
            }}
            isDisabled={isDisabled}
          >
            <HorizontalRadios options={dataDokonceni?.data.map((item) => ({ value: item.id.toString(), label: item.description }))} />
          </RadioGroupFormControl>
        </FormRow>
      </CustomCollapse>

      <CustomCollapse in={isOpenApartment}>
        <FormRow>
          {/* Č. bytu */}

          <InputFormControl name='property.data.realty.apartmentNumber'
            label='Č. bytu'
            placeholder="Např. 513"
            isRequired
            isDisabled={isDisabled}
            onBlur={(value) => {
              setValue('property.data.household.apartmentNumber', value as string)
            }}
          />

          <Grid gridTemplateColumns='1fr 1fr' gap={4}>
            {/* Poschodí */}
            <InputFormControl name='property.data.realty.apartmentFloor'
              label='Poschodí' labelProps={{ hint: 'Pro domácnost v přízemí, sklepním bytě nebo vyvýšeném přízemí uveďte hodnotu 0, jinak uveďte číslo patra. Pokud je domácnost ve více podlažích, uveďte to nejnižší. Hodnota může být vyžadována pojišťovnou pro určení rizika povodně.' }}
              placeholder="Např. 5"
              onBlur={(value) => {
                setValue('property.data.household.floor', value as number)
              }}
              isDisabled={isDisabled}
            />

            {isInsurersAvailable([Insurer.Axa, Insurer.GeneraliCeskaPojistovna]) && (
              // Poslední patro
              <FormControl>
                <FormLabel alignSelf='flex-start' tooltip="Zaškrtněte, pokud je byt v posledním poschodí bytového domu">
                  Poslední patro
                </FormLabel>
                {/* <FormHelperText>
                  Je v posledním poschodí
                </FormHelperText> */}

                <Switch
                  {...register('property.data.realty.isApartmentOnLastFloor')}
                  isChecked={watch('property.data.realty.isApartmentOnLastFloor') ?? false}
                  isDisabled={isDisabled}
                />
              </FormControl>
            )}
          </Grid>
        </FormRow>
      </CustomCollapse>

      {/* Typ konstrukce */}

      <RadioGroupFormControl name='property.data.realty.structuralType' valueAsNumber isRequired
        label='Typ konstrukce'
        labelProps={{
          hint: <>
            Vyberte typ konstrukce.
            <br />
            Pro pojišťovnu UNIQA:
            <br />
            Provedení Zdivo a Smíšené se vyznačuje nosnou konstrukcí
            z nehořlavých či převážně z nehořlavých materiálů (např.
            cihla, beton, panel) a nehořlavou střechou (např. pálené
            tašky, plech, eternit, nehořlavý plast apod.). Dřevěnou
            konstrukcí se rozumím především dřevostavby a stavby z
            jiných hořlavých materiálů, nebo stavby takového
            provedení, kde hořlavé konstrukce převládají z více než
            50 %.
          </>
        }}
        isDisabled={isDisabled}
      >
        <HorizontalRadios options={dataTypKonstrukce?.data.map(x => ({ value: x.id.toString(), label: x.description }))} />
      </RadioGroupFormControl>

      {/* Stav nemovitosti */}
      <RadioGroupFormControl
        name="property.data.realty.state"
        valueAsNumber
        isRequired
        label="Stav nemovitosti"
        labelProps={{ hint: 'Vyberte stav nemovitosti' }}
        isDisabled={isDisabled}
      >
        <HorizontalRadios
          options={dataStav?.data.map((item) => ({
            value: item.id.toString(),
            label: item.description,
          }))}
        />
      </RadioGroupFormControl>

      {isInsurersAvailable([
        Insurer.Axa,
        Insurer.CPP,
        Insurer.Csob,
        Insurer.Slavia,
        Insurer.Kooperativa,
        Insurer.GeneraliCeskaPojistovna,
        Insurer.Allianz,
        Insurer.Direct,
        Insurer.Pillow,
        Insurer.KB,
        Insurer.SV
      ]) && (
      // Dobrý technický stav
        <RadioGroupFormControl
          name="property.data.realty.goodState"
          valueAsBoolean
          isRequired
          label="Dobrý technický stav"
          labelProps={{
            hint: 'Vyberte zda je budova v dobrém technickém stavu. Budovy ve špatném technickém stavu není možné pojistit.',
          }}
          isDisabled={isDisabled}
        >
          <HorizontalRadios
            options={[
              { value: 'true', label: 'Ano' },
              { value: 'false', label: 'Ne' },
            ]}
          />
        </RadioGroupFormControl>
      )}

      <FormRow alignItems='end'>
        {/* Hodnota nemovitosti */}
        <NumberFormControl name='property.data.realty.realtyAmount' isRequired
          label='Hodnota nemovitosti'
          labelProps={{ hint: 'Zadejte hodnotu nemovitosti. Do hodnoty se započítávají zdi, omítky, podlahy, rozvody, topení, kuchyňská linka, dveře, koupelnové vybavení...' }}
          inputProps={{ placeholder: 'Např. 10 000 000' }}
          inputRightElement="Kč"
          w='full'
          isDisabled={isDisabled}
        />

        {/* Orientační poj. částka */}

        <Flex sx={{ gap: 4, alignItems: 'center', pb: 3 }}>
          <Button
            variant="link"
            isDisabled={isDisabled}
            onClick={() => calcRealtyAmount()}
            whiteSpace='nowrap'
          >
            <Calculate color="secondary.500" />
            Orientační poj. částka
          </Button>

          <CustomTooltip
            shouldWrapChildren
            maxW={500}
            hasArrow
            label="Jedná se o orientační pojistnou částku vypočítanou dle obecného vzorce, se započítaným nárůstem cen stavebních prací a materiálů, za kterou neneseme odpovědnost"
          >
            <IconButton variant="ghost" aria-label="">
              <QuestionMark />
            </IconButton>
          </CustomTooltip>

          {calcRealtyAmountError && (
            <Text variant={'error'}>{calcRealtyAmountError}</Text>
          )}
        </Flex>
      </FormRow>

      {/* Vedlejší budovy */}
      <RadioGroupFormControl name='property.data.realty.secondaryRealty' valueAsBoolean isRequired
        label='Vedlejší budovy'
        labelProps={{
          hint: 'Vyberte zda chcete pojistit vedlejší budovy. Pokud bude zvoleno NE, budou v nabídkách zobrazeny limity na pojištění vedlejších staveb zdarma dle aktuálních podmínek pojišťoven.'
        }}
        isDisabled={isDisabled}
      >
        <HorizontalRadios options={[{ value: 'true', label: 'Ano' }, { value: 'false', label: 'Ne' }]} />
      </RadioGroupFormControl>

      <CustomCollapse in={secondaryRealty == true}>

        <Box sx={{ display: 'grid', gap: 4 }}>
          {fieldsSecondaryRealties.map((field, index) => (
            <React.Fragment key={field.id}>
              {/* <Divider borderColor="gray" /> */}

              <Flex columnGap={4}>
                <SelectFormControl label="Typ stavby"
                  name={`property.data.secondaryRealties.${index}.type`} control={control}
                  options={dataBuildingType?.data.map(item => ({ value: item.id.toString(), label: item.description! })) ?? []}
                  isDisabled={isDisabled}
                  unselectedText='Vyberte typ'
                />

                <NumberFormControl label="Hodnota nemovitosti"
                  name={`property.data.secondaryRealties.${index}.secondaryRealtyAmount`} control={control}
                  isDisabled={isDisabled}
                  inputRightElement="Kč" inputProps={{ placeholder: 'Např. 3 000 000' }} isRequired
                />

                <InputFormControl label="Popis"
                  name={`property.data.secondaryRealties.${index}.description`} control={control}
                  isDisabled={isDisabled}
                />

                {!isDisabled && fieldsSecondaryRealties.length > 1 && (
                  <Flex sx={{ gap: 4, mt: 4, alignItems: 'center' }}>
                    <Button
                      variant="link"
                      onClick={() => removeSecondaryRealty(index)}
                    >
                      <Trash color="secondary.500" />
                    </Button>
                  </Flex>
                )}

              </Flex>
            </React.Fragment>
          ))}

          {/* <Divider borderColor="gray" /> */}

          {!isDisabled && (
            <Box>
              <Button
                type="button"
                variant="link"
                onClick={() => appendSecondaryRealty({} as SecondaryRealtyDto)}
              >
                <Plus color="secondary.500" />
                Přidat další budovu
              </Button>
            </Box>
          )}

          {isInsurersAvailable([
            Insurer.KB
          ]) && (
            <FormRow>
              <NumberFormControl name='property.data.realty.secondaryBuildingsArea' isRequired
                label='Celková plocha vedlejších budov' labelProps={{ hint: 'Souhrnná plocha všech vedlejších staveb' }}
                inputRightElement="㎡"
                w='100%'
                inputProps={{
                  placeholder: 'Např. 128'
                }}
                isDisabled={isDisabled}
              />
            </FormRow>
          )}
        </Box>
      </CustomCollapse>
    </Grid >
  )
}